import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip,
  Table, TableBody, TableRow, TableCell, Typography, CircularProgress, Divider
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useVersionsStore } from "../store/versionsStore.js";
import { useApi } from "../hooks/useApi.js";

interface SvcVer { name: string; version: string; ok: boolean }
interface VersionsResp { services: SvcVer[]; keycloak: SvcVer; generatedAt?: string }

// Dialog "Versioni" (easter egg): elenco di tutti i servizi con la relativa versione,
// più Keycloak e il frontend. Si apre con 5 tap sul logo nella sidebar.
export function VersionsDialog() {
  const open = useVersionsStore((s) => s.open);
  const setOpen = useVersionsStore((s) => s.setOpen);
  const { t } = useTranslation();
  const { call } = useApi();
  const [data, setData] = useState<VersionsResp | null>(null);
  const [fe, setFe] = useState<{ appVersion?: string; version?: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setLoading(true);
    call<VersionsResp>("/.well-known/versions", { silent: true, noLoader: true })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" })
      .then((r) => r.json())
      .then(setFe)
      .catch(() => setFe(null));
  }, [open, call]);

  const row = (name: string, version: string, ok: boolean) => (
    <TableRow key={name}>
      <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
      <TableCell sx={{ fontFamily: "monospace" }}>{version}</TableCell>
      <TableCell align="right">
        <Chip size="small" color={ok ? "success" : "default"} label={ok ? t("versions.ok") : t("versions.ko")} />
      </TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>🐴 {t("versions.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="overline" color="text.secondary">{t("versions.frontend")}</Typography>
        <Table size="small">
          <TableBody>
            {row("web", fe?.appVersion ?? "n/d", true)}
            <TableRow>
              <TableCell sx={{ color: "text.secondary" }}>{t("versions.build")}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }} colSpan={2}>{fe?.version ?? "n/d"}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">{t("versions.services")}</Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <Table size="small">
            <TableBody>
              {(data?.services ?? []).map((s) => row(s.name, s.version, s.ok))}
              {data?.keycloak && row(data.keycloak.name, data.keycloak.version, data.keycloak.ok)}
              {!data && <TableRow><TableCell colSpan={3} sx={{ color: "text.secondary" }}>{t("versions.unavailable")}</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
